/**
 * Pure reads over GameState: object lookup, filters, conditions, amounts,
 * static collection and effective stats. Nothing in this file mutates.
 *
 * Nothing here branches on a card name or id.
 */

import type {
  Amount,
  CardType,
  Condition,
  Filter,
  Keyword,
  StaticEffect,
  Who,
} from './cardTypes';
import { profileOf, totalCost, type ObjectProfile } from './cardDb';
import type { EffectiveStats, GameObject, GameState, PlayerState } from './types';
import { CHAINS } from './types';

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function obj(state: GameState, instanceId: string): GameObject | undefined {
  return state.objects[instanceId];
}

export function requireObj(state: GameState, instanceId: string): GameObject {
  const o = state.objects[instanceId];
  if (!o) throw new Error(`Unknown instance ${instanceId}`);
  return o;
}

export function player(state: GameState, playerId: string): PlayerState {
  const p = state.players[playerId];
  if (!p) throw new Error(`Unknown player ${playerId}`);
  return p;
}

export function isPlayerId(state: GameState, id: string): boolean {
  return Object.prototype.hasOwnProperty.call(state.players, id);
}

export function opponentOf(state: GameState, playerId: string): string {
  const other = state.playerOrder.find((p) => p !== playerId);
  return other ?? playerId;
}

export function profile(state: GameState, instanceId: string): ObjectProfile {
  const o = state.objects[instanceId];
  if (!o) return profileOf({});
  return profileOf(o);
}

export function nameOf(state: GameState, instanceId: string): string {
  return profile(state, instanceId).name;
}

/** Display name for a player id or an instance id, for events and the log. */
export function labelOf(state: GameState, id: string): string {
  if (isPlayerId(state, id)) return state.players[id].name;
  return nameOf(state, id);
}

/** Every battlefield object of every player, in a stable order. */
export function allBattlefield(state: GameState): GameObject[] {
  const out: GameObject[] = [];
  for (const pid of state.playerOrder) {
    const p = state.players[pid];
    for (const id of p.battlefield.wallets) pushIf(out, state.objects[id]);
    for (const id of p.battlefield.characters) pushIf(out, state.objects[id]);
    for (const id of p.battlefield.protocols) pushIf(out, state.objects[id]);
    for (const id of p.battlefield.upgrades) pushIf(out, state.objects[id]);
  }
  return out;
}

export function charactersOf(state: GameState, playerId: string): GameObject[] {
  const out: GameObject[] = [];
  for (const id of state.players[playerId].battlefield.characters) pushIf(out, state.objects[id]);
  return out;
}

function pushIf(list: GameObject[], o: GameObject | undefined): void {
  if (o) list.push(o);
}

/** Which battlefield row a type belongs to. Upgrades get their own row. */
export function rowFor(type: CardType): 'characters' | 'wallets' | 'protocols' | 'upgrades' | null {
  switch (type) {
    case 'character':
      return 'characters';
    case 'wallet':
      return 'wallets';
    case 'protocol':
      return 'protocols';
    case 'upgrade':
      return 'upgrades';
    default:
      return null;
  }
}

export function isPermanentType(type: CardType): boolean {
  return rowFor(type) !== null;
}

// ---------------------------------------------------------------------------
// Statics
// ---------------------------------------------------------------------------

export interface ActiveStatic {
  source: GameObject;
  controller: string;
  effect: StaticEffect;
}

/**
 * Every static ability currently active, that is: its source is on the
 * battlefield and its `condition` (if any) holds.
 */
export function collectStatics(state: GameState): ActiveStatic[] {
  const out: ActiveStatic[] = [];
  for (const o of allBattlefield(state)) {
    const prof = profileOf(o);
    for (const ability of prof.abilities) {
      if (ability.kind !== 'static') continue;
      if (ability.condition && !evalCondition(state, ability.condition, o.controller, o.instanceId)) {
        continue;
      }
      out.push({ source: o, controller: o.controller, effect: ability.effect });
    }
    // An Upgrade grants keywords to its host through its `upgrade` block, which
    // is handled in getEffectiveStats, not here.
  }
  return out;
}

// ---------------------------------------------------------------------------
// Effective stats
// ---------------------------------------------------------------------------

interface StatsOptions {
  /** false while evaluating a filter, to avoid static/filter recursion. */
  includeStatics: boolean;
}

/**
 * Layer base stats, attached upgrades, static buffs and temporary buffs, then
 * keywords from every layer, then keyword removals.
 */
export function getEffectiveStats(
  state: GameState,
  instanceId: string,
  options: StatsOptions = { includeStatics: true },
): EffectiveStats {
  const o = state.objects[instanceId];
  if (!o) return { attack: 0, health: 0, keywords: [], cantBlock: false, damageBonus: 0 };
  const prof = profileOf(o);

  let attack = prof.baseAttack;
  let health = prof.baseHealth;
  const keywords = new Set<Keyword>(prof.printedKeywords);

  // Layer 1: attached upgrades.
  if (o.zone === 'battlefield') {
    for (const upId of state.players[o.controller].battlefield.upgrades) {
      const up = state.objects[upId];
      if (!up || up.attachedTo !== instanceId) continue;
      const upProf = profileOf(up);
      if (!upProf.upgrade) continue;
      attack += upProf.upgrade.attack;
      health += upProf.upgrade.health;
      for (const k of upProf.upgrade.keywords) keywords.add(k);
    }
  }

  // Layer 2: static buffs and keyword grants.
  let cantBlock = false;
  let damageBonus = 0;
  if (options.includeStatics && o.zone === 'battlefield') {
    for (const st of collectStatics(state)) {
      const e = st.effect;
      switch (e.type) {
        case 'buff':
          if (matchesFilter(state, o, e.filter, e.who, st.controller, st.source.instanceId)) {
            attack += e.attack;
            health += e.health;
          }
          break;
        case 'grantKeyword':
          if (matchesFilter(state, o, e.filter, e.who, st.controller, st.source.instanceId)) {
            keywords.add(e.keyword);
          }
          break;
        case 'cantBlock':
          if (st.source.instanceId === instanceId) cantBlock = true;
          break;
        case 'damageModifier': {
          const who: Who = e.who;
          if (matchesFilter(state, o, e.filter, who, st.controller, st.source.instanceId)) {
            damageBonus += e.amount;
          }
          break;
        }
        default:
          break;
      }
    }
  }

  // Layer 3: temporary and permanent buffs from one-shot effects.
  for (const b of o.buffs) {
    attack += b.attack;
    health += b.health;
  }

  // Layer 4: keyword grants and removals from one-shot effects.
  for (const g of o.grantedKeywords) keywords.add(g.keyword);
  for (const r of o.removedKeywords) keywords.delete(r.keyword);

  return {
    attack: Math.max(0, attack),
    health,
    keywords: [...keywords],
    cantBlock,
    damageBonus,
  };
}

export function hasKeyword(state: GameState, instanceId: string, keyword: Keyword): boolean {
  return getEffectiveStats(state, instanceId).keywords.includes(keyword);
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

function whoMatches(who: Who | undefined, objController: string, abilityController: string): boolean {
  switch (who ?? 'any') {
    case 'friendly':
      return objController === abilityController;
    case 'enemy':
      return objController !== abilityController;
    default:
      return true;
  }
}

/**
 * Does `o` satisfy `filter` for an ability controlled by `abilityController`
 * whose source is `sourceInstanceId`? All present fields must match.
 */
export function matchesFilter(
  state: GameState,
  o: GameObject,
  filter: Filter | undefined,
  who: Who | undefined,
  abilityController: string,
  sourceInstanceId?: string,
): boolean {
  if (!whoMatches(who, o.controller, abilityController)) return false;
  if (!filter) return true;
  const prof = profileOf(o);

  if (filter.excludeSelf && sourceInstanceId && o.instanceId === sourceInstanceId) return false;
  if (filter.types && !filter.types.includes(prof.type)) return false;
  if (filter.subtypes && !filter.subtypes.some((s) => prof.subtypes.includes(s))) return false;
  if (filter.faction && !filter.faction.includes(prof.faction)) return false;

  if (filter.keywords || filter.lacksKeywords) {
    const stats = getEffectiveStats(state, o.instanceId, { includeStatics: false });
    if (filter.keywords && !filter.keywords.every((k) => stats.keywords.includes(k))) return false;
    if (filter.lacksKeywords && filter.lacksKeywords.some((k) => stats.keywords.includes(k))) {
      return false;
    }
  }

  if (filter.minCost !== undefined || filter.maxCost !== undefined) {
    const c = totalCost(prof.cost);
    if (filter.minCost !== undefined && c < filter.minCost) return false;
    if (filter.maxCost !== undefined && c > filter.maxCost) return false;
  }

  if (
    filter.minAttack !== undefined ||
    filter.maxAttack !== undefined ||
    filter.minHealth !== undefined ||
    filter.maxHealth !== undefined
  ) {
    const stats = getEffectiveStats(state, o.instanceId, { includeStatics: false });
    if (filter.minAttack !== undefined && stats.attack < filter.minAttack) return false;
    if (filter.maxAttack !== undefined && stats.attack > filter.maxAttack) return false;
    if (filter.minHealth !== undefined && stats.health < filter.minHealth) return false;
    if (filter.maxHealth !== undefined && stats.health > filter.maxHealth) return false;
  }

  if (filter.isToken !== undefined && prof.isToken !== filter.isToken) return false;
  if (filter.isLegendary !== undefined && prof.legendary !== filter.isLegendary) return false;
  if (filter.exhausted !== undefined && o.exhausted !== filter.exhausted) return false;
  if (filter.attacking !== undefined && (o.attacking === true) !== filter.attacking) return false;
  if (filter.blocking !== undefined && (o.blocking !== undefined) !== filter.blocking) return false;
  if (filter.damaged !== undefined && o.damage > 0 !== filter.damaged) return false;

  return true;
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

function inRange(value: number, min?: number, max?: number): boolean {
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
}

export function evalCondition(
  state: GameState,
  condition: Condition,
  controller: string,
  sourceInstanceId?: string,
): boolean {
  switch (condition.kind) {
    case 'control': {
      const n = allBattlefield(state).filter((o) =>
        matchesFilter(state, o, condition.filter, condition.who, controller, sourceInstanceId),
      ).length;
      return inRange(n, condition.min ?? 1, condition.max);
    }
    case 'integrity': {
      const pid = condition.who === 'controller' ? controller : opponentOf(state, controller);
      return inRange(state.players[pid].integrity, condition.min, condition.max);
    }
    case 'turn':
      return inRange(state.turn, condition.min, condition.max);
    case 'dust': {
      const n = countDust(state, condition.who, condition.filter, controller, sourceInstanceId);
      return inRange(n, condition.min ?? 1, undefined);
    }
    case 'handSize': {
      const pid = condition.who === 'controller' ? controller : opponentOf(state, controller);
      return inRange(state.players[pid].hand.length, condition.min, condition.max);
    }
    case 'wallets': {
      const pid = condition.who === 'controller' ? controller : opponentOf(state, controller);
      const n = countWallets(state, pid, condition.chain);
      return inRange(n, condition.min, condition.max);
    }
    case 'playedCardThisTurn': {
      const played = state.players[controller].playedThisTurn;
      const n = condition.types ? played.filter((t) => condition.types?.includes(t)).length : played.length;
      return inRange(n, condition.min ?? 1, undefined);
    }
    case 'sourceDamaged': {
      if (!sourceInstanceId) return false;
      const o = state.objects[sourceInstanceId];
      return !!o && o.damage > 0;
    }
    case 'not':
      return !evalCondition(state, condition.condition, controller, sourceInstanceId);
    case 'all':
      return condition.conditions.every((c) => evalCondition(state, c, controller, sourceInstanceId));
    case 'any':
      return condition.conditions.some((c) => evalCondition(state, c, controller, sourceInstanceId));
    default:
      return false;
  }
}

export function countWallets(state: GameState, playerId: string, chain?: string): number {
  let n = 0;
  for (const id of state.players[playerId].battlefield.wallets) {
    const o = state.objects[id];
    if (!o) continue;
    if (!chain) {
      n++;
      continue;
    }
    const w = profileOf(o).wallet;
    if (w && (w.chain === chain || w.chain === 'any')) n++;
  }
  return n;
}

function countDust(
  state: GameState,
  who: Who,
  filter: Filter,
  controller: string,
  sourceInstanceId?: string,
): number {
  let n = 0;
  for (const pid of state.playerOrder) {
    for (const id of state.players[pid].dust) {
      const o = state.objects[id];
      if (!o) continue;
      if (matchesFilter(state, o, filter, who, controller, sourceInstanceId)) n++;
    }
  }
  return n;
}

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

export function evalAmount(
  state: GameState,
  amount: Amount,
  controller: string,
  sourceInstanceId?: string,
  xValue?: number,
): number {
  if (typeof amount === 'number') return amount;
  switch (amount.kind) {
    case 'count': {
      const n = allBattlefield(state).filter((o) =>
        matchesFilter(state, o, amount.filter, amount.who, controller, sourceInstanceId),
      ).length;
      return n * (amount.times ?? 1) + (amount.plus ?? 0);
    }
    case 'countDust': {
      const n = countDust(state, amount.who, amount.filter, controller, sourceInstanceId);
      return n * (amount.times ?? 1) + (amount.plus ?? 0);
    }
    case 'handSize': {
      const pid = amount.who === 'controller' ? controller : opponentOf(state, controller);
      return state.players[pid].hand.length;
    }
    case 'walletCount': {
      const pid = amount.who === 'controller' ? controller : opponentOf(state, controller);
      return countWallets(state, pid, amount.chain);
    }
    case 'sourceAttack':
      return sourceInstanceId ? getEffectiveStats(state, sourceInstanceId).attack : 0;
    case 'sourceHealth':
      return sourceInstanceId ? getEffectiveStats(state, sourceInstanceId).health : 0;
    case 'x':
      return xValue ?? (sourceInstanceId ? state.objects[sourceInstanceId]?.xValue ?? 0 : 0);
    default:
      return 0;
  }
}

// ---------------------------------------------------------------------------
// Misc rule reads
// ---------------------------------------------------------------------------

/** Wallet installs allowed this turn, base 1 plus `extraWalletInstall` statics. */
export function walletInstallAllowance(state: GameState, playerId: string): number {
  let n = 1;
  for (const st of collectStatics(state)) {
    if (st.effect.type === 'extraWalletInstall' && st.controller === playerId) {
      n += st.effect.amount;
    }
  }
  return n;
}

export function handSizeLimit(state: GameState, playerId: string): number {
  let n = state.options.handSizeLimit;
  for (const st of collectStatics(state)) {
    if (st.effect.type === 'handSizeModifier' && st.controller === playerId) {
      n += st.effect.amount;
    }
  }
  return Math.max(0, n);
}

/** Extra gas a given wallet produces from `walletsProduceExtra` statics. */
export function walletExtraProduction(state: GameState, walletInstanceId: string): number {
  const o = state.objects[walletInstanceId];
  if (!o) return 0;
  const w = profileOf(o).wallet;
  if (!w) return 0;
  let extra = 0;
  for (const st of collectStatics(state)) {
    if (st.effect.type !== 'walletsProduceExtra') continue;
    if (st.controller !== o.controller) continue;
    if (w.chain === st.effect.chain || w.chain === 'any') extra += st.effect.amount;
  }
  return extra;
}

/** True when the opponent's characters must enter exhausted. */
export function entersExhaustedByStatic(state: GameState, enteringController: string): boolean {
  for (const st of collectStatics(state)) {
    if (st.effect.type !== 'opponentCharactersEnterExhausted') continue;
    if (st.controller !== enteringController) return true;
  }
  return false;
}

/** Characters that must attack this combat, from `attackRequirement` statics. */
export function attackRequirements(state: GameState, attackingPlayer: string): string[] {
  const ids: string[] = [];
  for (const st of collectStatics(state)) {
    if (st.effect.type !== 'attackRequirement') continue;
    if (st.controller === attackingPlayer) continue;
    for (const c of charactersOf(state, attackingPlayer)) {
      if (matchesFilter(state, c, st.effect.filter, 'any', st.controller, st.source.instanceId)) {
        if (!ids.includes(c.instanceId)) ids.push(c.instanceId);
      }
    }
  }
  return ids;
}

/** Filters that exclude blockers of a given attacker (`cantBeBlockedBy`). */
export function cantBeBlockedByFilters(state: GameState, attackerId: string): Filter[] {
  const out: Filter[] = [];
  const a = state.objects[attackerId];
  if (!a) return out;
  for (const ability of profileOf(a).abilities) {
    if (ability.kind === 'static' && ability.effect.type === 'cantBeBlockedBy') {
      if (ability.condition && !evalCondition(state, ability.condition, a.controller, a.instanceId)) {
        continue;
      }
      out.push(ability.effect.filter);
    }
  }
  return out;
}

export function isChainId(value: string): boolean {
  return (CHAINS as string[]).includes(value);
}
